import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import storage from '../../hooks/useStorage/useStorage';
import LangEnum from '../../lang/LangEnum';
import { removeOrderSnapshot } from '../../modules/catalogs/products/hooks/SnapshotController/snapshotController.utils';
import { StorageEnum } from '../../modules/context/storage';
import { formatOrderInOrderBookOrder } from '../../modules/order-book/utils/formatters';
import {
  ToggleShopping,
  ToggleShoppingKitParams,
} from '../../modules/orders/contexts/orderApplication.types';
import { Order, StatusType } from '../../modules/orders/order.types';
import { OrderView } from '../../modules/orders/ordersView.types';
import { getOrderView } from '../../modules/orders/ordersView.utils';
import { ordersRepository } from '../../modules/orders/repositories/ordersRepository/orders.repository';
import { kitActionsRepository } from '../../modules/products/repositories/kitActions/kitActions.repository';
import { productActionsRepository } from '../../modules/products/repositories/productActions/productActions.repository';
import { ProductActionsRequestParams } from '../../modules/products/repositories/types/productActions.types';
import PATHS from '../../routes/routes.const';
import { restApi } from '../../services/api';
import { getOrderSummary } from '../../services/modules/order/order.repository';
import { IAffectedOrderProducts } from '../../services/modules/order/order.types';
import { findSimpleOrdersList } from '../../services/modules/orders/orders.repository';
import { useAppEnvironmentStore } from '../AppEnvironment/appEnvironment.store';
import useSharedEntityStore from '../SharedEntities/sharedEntities.store';
import snackbarStore from '../Snackbar.store';
import createSelectors from '../createSelectors';
import {
  GetOrderProductIds,
  HandleChangeSelectedOrderParams,
  ICreateOrder,
  IStorageItems,
  IsOrderActivableParams,
  OrderBookCustomer,
  OrderBookStateEnum,
  OrderBookStorage,
  OrderBookStore,
  ReloadOrderItemsQuantityBookParams,
  RemoveOrderCatalogSnapshotParams,
  SelectedOrderSummary,
} from './orderBook.types';
import { orderBookStoreRepository } from './repository/orderBookStore.repository';

const PERSISTER_KEY = '_orderbook';

const removeOrderCatalogSnapshot = ({
  orderId,
  teamId,
  isOfflineMode,
}: RemoveOrderCatalogSnapshotParams) => {
  if (!teamId) {
    return;
  }

  removeOrderSnapshot({
    orderId,
    teamId,
    sharedEntityTeamId: useSharedEntityStore.getState().sharedEntity?.teamId,
    isOfflineMode,
  });
};

const useOrderBookStore = create<OrderBookStore>()(
  persist(
    (set, get) => ({
      loading: false,
      loadingOrder: false,
      isBlockOrderBook: false,
      orderAutocompleteFocused: false,
      customer: undefined,
      loadingOrderItems: false,
      orderBookState: false,
      orderBookModalState: false,
      orderBookTimeline: OrderBookStateEnum.INITIAL_STATE,
      selectedOrder: undefined,
      selectedOrderSummary: undefined,
      selectedOrderItemsQuantity: 0,
      orderProductsIds: {
        productColorIds: [],
        kitIds: [],
      },
      orders: [],
      otherOrdersProductsIds: [],
      lastCreatedOrderId: undefined,
      salesOpportunityInterceptorId: undefined,
      isOfflineMode: false,
      hideOrderValue: localStorage.getItem(StorageEnum.SHOW_ORDER_VALUE) === 'true',

      reloadSelectedOrderSummary: async () => {
        const { selectedOrder } = get();

        if (!selectedOrder) {
          return;
        }

        const calculateResponse = await getOrderSummary({ orderId: selectedOrder?.id });

        const summaryTotal = calculateResponse.data?.calculate?.total;
        if (summaryTotal) {
          const { total, subtotal, pieces } = summaryTotal;
          set({ selectedOrderSummary: { total, subtotal, pieces } });
        }
      },

      toggleShoppingKit: async ({
        kitId,
        translate,
        getApiErrorMessage,
      }: ToggleShoppingKitParams): Promise<void> => {
        const { handleMessage } = snackbarStore;

        const {
          selectedOrder,
          isOfflineMode,
          orderProductsIds,
          isShopping,
          reloadSelectedOrderSummary,
          fetchOrderProductIds,
        } = get();

        if (!selectedOrder) {
          return;
        }

        const isCurrentlyShopping = isShopping(kitId, orderProductsIds);

        const params = {
          kitId,
          orderId: selectedOrder.id,
        };

        const newOrderProductsIds = isCurrentlyShopping
          ? {
              ...orderProductsIds,
              kitIds: orderProductsIds.kitIds.filter((id) => id !== kitId),
            }
          : {
              ...orderProductsIds,
              kitIds: [...(orderProductsIds.kitIds || []), kitId],
            };

        set({ orderProductsIds: newOrderProductsIds });

        const repository = kitActionsRepository(isOfflineMode);
        const response = isCurrentlyShopping
          ? await repository.unshopKit(params)
          : await repository.shopKit(params);

        if (!response?.ok) {
          const errorMessage = getApiErrorMessage(
            response,
            translate(LangEnum.SNACKBAR_UNABLE_TO_CHANGE_ORDER),
          );
          handleMessage({
            type: 'error',
            message: errorMessage,
          });

          set({ orderProductsIds });

          await reloadSelectedOrderSummary();
          return;
        }

        const orderProductsIdsAfterUpdateOrder = await fetchOrderProductIds(selectedOrder.id);
        set({
          orderProductsIds: orderProductsIdsAfterUpdateOrder || { kitIds: [], productColorIds: [] },
        });

        await reloadSelectedOrderSummary();
      },

      toggleShopping: async ({
        productColorId,
        translate,
        onSuccess,
        onFailure,
      }: ToggleShopping): Promise<void> => {
        const { handleMessage } = snackbarStore;
        const {
          orders,
          selectedOrder,
          orderProductsIds,
          orderBookTimeline,
          selectedOrderSummary,
          otherOrdersProductsIds,
          isShopping,
          reloadSelectedOrderSummary,
        } = get();

        if (!selectedOrder) {
          return;
        }

        const isCurrentlyShopping = isShopping(productColorId, orderProductsIds);

        const params: ProductActionsRequestParams = {
          productColorId,
          orderId: selectedOrder?.id,
        };

        const newOrderProductsIds = isCurrentlyShopping
          ? {
              ...orderProductsIds,
              productColorIds: orderProductsIds.productColorIds.filter(
                (id) => id !== productColorId,
              ),
            }
          : {
              ...orderProductsIds,
              productColorIds: [...(orderProductsIds.productColorIds || []), productColorId],
            };

        set({ orderProductsIds: newOrderProductsIds });

        const response = isCurrentlyShopping
          ? await productActionsRepository().unshopProduct(params)
          : await productActionsRepository().shopProduct(params);

        if (isCurrentlyShopping) {
          await reloadSelectedOrderSummary();
        }

        if (!response?.ok) {
          const errorMessage =
            response?.data?.message || translate(LangEnum.SNACKBAR_UNABLE_TO_CHANGE_ORDER);
          handleMessage({
            type: 'error',
            message: errorMessage,
          });

          const updatedOrderBook = {
            orders,
            orderBookTimeline,
            selectedOrder,
            selectedOrderSummary,
            otherOrdersProductsIds,
            orderProductsIds,
          };
          set({ ...updatedOrderBook });

          onFailure?.([productColorId]);
        }

        onSuccess?.(productColorId);
      },

      isShopping: (itemId: string, orderProductsIds: GetOrderProductIds) => {
        const productColorIdsIncludes = orderProductsIds.productColorIds.includes(itemId);
        const kitIdsIncludes = orderProductsIds.kitIds.includes(itemId);

        return productColorIdsIncludes || kitIdsIncludes;
      },

      fetchOrderProductIds: async (orderId?: string) => {
        if (!orderId) {
          return;
        }
        const { isOfflineMode } = get();
        const res = await orderBookStoreRepository(isOfflineMode).getOrderProductIds(orderId);

        if (!res.ok) {
          return;
        }

        return res.data;
      },

      fetchOrder: (orderId: string) => restApi.get<Order>(`/orders/${orderId}`),

      validateRehydratedOrders: async () => {
        const { selectedOrder, orders, handleRemoveOrder, isOfflineMode } = get();

        if (isOfflineMode) {
          return;
        }

        const allOrders = selectedOrder ? [selectedOrder, ...orders] : orders;

        if (allOrders.length === 0) {
          return;
        }

        const orderIds = allOrders.map((order) => order.id);
        const response = await findSimpleOrdersList({
          ids: orderIds,
          limit: orderIds.length,
          skip: 0,
        });

        if (!response.ok || !response.data) {
          return;
        }

        const existingOrderIds = new Set(response.data.data.map((order) => order.id));
        const invalidOrderIds = orderIds.filter((id) => !existingOrderIds.has(id));

        if (invalidOrderIds.length > 0) {
          const teamId = storage.getTeamId();

          invalidOrderIds.forEach((orderId) => {
            handleRemoveOrder(orderId, teamId);
          });
        }
      },

      handleOpenOrderBook: () => set({ orderBookState: true }),

      handleChangeBlockOrderBook: (flag: boolean) => set({ isBlockOrderBook: flag }),

      setOrderBookOfflineMode: (flag: boolean) => set({ isOfflineMode: flag }),

      handleCloseOrderBook: () => {
        const { selectedOrder } = get();
        const orderBookState = selectedOrder
          ? OrderBookStateEnum.LIST_ORDERS
          : OrderBookStateEnum.INITIAL_STATE;

        set({
          orderBookState: false,
          orderBookModalState: false,
          orderBookTimeline: orderBookState,
        });
      },

      handleOpenOrderBookModal: () => set({ orderBookModalState: true }),

      handleCloseOrderBookModal: () => set({ orderBookModalState: false }),

      handleAddOrder: (order: Order) => {
        const { orders } = get();
        const newOrder = formatOrderInOrderBookOrder(order);
        set({ orders: [newOrder, ...orders] });
      },

      handleChangeOrderBookTimeline: (newState: OrderBookStateEnum) => {
        set({ orderBookTimeline: newState });
      },

      handleRemoveAllOrders: (team) => {
        const { selectedOrder, orders, isOfflineMode } = get();

        if (!selectedOrder) {
          return;
        }

        for (const order of orders) {
          removeOrderCatalogSnapshot({ orderId: order.id, teamId: team?.id, isOfflineMode });
        }

        set({ orders: [] });
      },

      updateSelectedOrderSummary: (summary: SelectedOrderSummary) => {
        const { selectedOrder } = get();

        if (!selectedOrder) {
          return;
        }
        set({ selectedOrderSummary: summary });
      },

      handleChangeSelectedOrder: async (params: HandleChangeSelectedOrderParams) => {
        const {
          orders,
          fetchOrderProductIds,
          selectedOrder,
          selectedOrderSummary,
          otherOrdersProductsIds,
          isOfflineMode,
        } = get();
        const { order, teamId, redirectOrder, finishedOrder } = params;
        const orderBookOrder = formatOrderInOrderBookOrder(order);
        const isSameOrder = orderBookOrder.id === selectedOrder?.id;

        set({ loadingOrderItems: true, isBlockOrderBook: true });

        const promisesToSolve = [fetchOrderProductIds(orderBookOrder.id)];

        if (!finishedOrder) {
          promisesToSolve.push(fetchOrderProductIds(selectedOrder?.id));
        }

        const [newSelectedOrderResponse, oldSelectedOrderResponse] =
          await Promise.all(promisesToSolve);

        const newSelectedOrderProductIds = newSelectedOrderResponse || {
          productColorIds: [],
          kitIds: [],
        };
        const oldSelectedOrderProductIds = oldSelectedOrderResponse || {
          productColorIds: [],
          kitIds: [],
        };

        const newOrders = orders.filter((o) => orderBookOrder?.id !== o.id);
        const oldTotalItems = oldSelectedOrderProductIds.productColorIds.length;

        if (selectedOrder && !finishedOrder && !isSameOrder) {
          newOrders?.unshift({
            ...selectedOrder,
            totalItems: oldTotalItems,
            totalValue: selectedOrderSummary?.total || 0,
            subTotal: selectedOrderSummary?.subtotal || selectedOrder.subTotal,
            totalPieces: selectedOrderSummary?.pieces || selectedOrder.totalPieces,
          });
        }

        if (finishedOrder && selectedOrder) {
          removeOrderCatalogSnapshot({ orderId: selectedOrder.id, teamId, isOfflineMode });
        }

        const updatedOrderBook = {
          orders: finishedOrder ? newOrders?.filter((o) => o.id !== orderBookOrder.id) : newOrders,
          orderBookTimeline: OrderBookStateEnum.LIST_ORDERS,
          selectedOrder: orderBookOrder,
          otherOrdersProductsIds,
          orderProductsIds: newSelectedOrderProductIds,
          selectedOrderSummary: {
            total: orderBookOrder?.totalValue || 0,
            subtotal: orderBookOrder?.subTotal,
            pieces: orderBookOrder?.totalPieces,
          },
        };

        set({
          ...updatedOrderBook,
        });

        set({ loadingOrderItems: false, isBlockOrderBook: false });

        if (redirectOrder) {
          redirectOrder();
        }
      },

      handleOrderAutocompleteFocused: (flag: boolean) => {
        set({ orderAutocompleteFocused: flag });
      },

      startLoadOrder: () => set({ loadingOrder: true }),

      startLoadOrderItems: () => set({ loadingOrderItems: true }),

      isOrderActivable: ({
        orderStatus: status,
        synchronized,
        role,
        team,
      }: IsOrderActivableParams) => {
        const appEnvironment = useAppEnvironmentStore.getState().appEnvironment;

        const orderStatus = getOrderView({
          role,
          appEnvironment,
          orderStatus: status as StatusType,
          integrations: team?.brand?.integrations,
          orderIntegrationStatus: synchronized,
        });
        if (orderStatus === OrderView.EDIT) {
          return true;
        }

        return false;
      },

      createOrder: async (params: ICreateOrder) => {
        const { history, customerId, sellerId, translate, team, isSuggested } = params;
        const { handleChangeSelectedOrder, handleCloseOrderBookModal, handleCloseOrderBook } =
          get();
        const { handleMessage } = snackbarStore;
        const currentRoute = history.location.pathname;
        const onEdit = currentRoute.includes(OrderView.EDIT);

        set({ loadingOrder: true });
        const { data: order, ok } = await ordersRepository().create({
          customerId,
          sellerId,
          isSuggested,
        });

        if (order && ok) {
          handleChangeSelectedOrder({
            translate,
            teamId: team?.id,
            order,
            redirectOrder: () =>
              onEdit ? history.push(`${PATHS.main.orders.path}/${order.id}/${OrderView.EDIT}`) : {},
          });
        }
        set({ loadingOrder: false });

        if (params.onSuccess) {
          params.onSuccess(order.id);
        }
        if (!ok && translate) {
          const errorMessage = order?.code ? translate(order.code) : order?.message;
          handleMessage({
            type: 'error',
            message:
              errorMessage ??
              translate(LangEnum.API_EXCEPTION_ORDER_UNPROCESSABLE_CUSTOMER_ON_CREATE_ORDER),
          });
        }

        if (!ok && order?.code === LangEnum.API_EXCEPTION_ORDER_ACCESS_DENIED) {
          handleCloseOrderBookModal();
          handleCloseOrderBook();
        }
      },

      reloadOrderItemsQuantity: async (
        calculate: boolean,
        orderId?: string,
        isSelectedOrder?: boolean,
      ): Promise<IAffectedOrderProducts[] | undefined> => {
        const {
          selectedOrder,
          selectedOrderSummary,
          orders,
          fetchOrderProductIds,
          orderBookTimeline,
          otherOrdersProductsIds,
          reloadOrderItemsQuantityBook,
        } = get();

        if (!selectedOrder) {
          return;
        }

        if (!isSelectedOrder && orderId) {
          const affectedOrderProducts = reloadOrderItemsQuantityBook({ calculate, orderId });
          return affectedOrderProducts;
        }

        let newSelectedOrderSummary = selectedOrderSummary;

        const productIds: GetOrderProductIds = (await fetchOrderProductIds(
          orderId || selectedOrder.id,
        )) || { productColorIds: [], kitIds: [] };

        const affectedOrderProducts: IAffectedOrderProducts[] = [];
        if (calculate) {
          const calculateResponse = await getOrderSummary({ orderId: orderId || selectedOrder.id });
          if (calculateResponse.data) {
            const summaryTotal = calculateResponse.data.calculate?.total;
            if (summaryTotal) {
              const { total, subtotal, pieces } = summaryTotal;
              newSelectedOrderSummary = { total, subtotal, pieces };
            }
            affectedOrderProducts.push(...(calculateResponse.data.affectedOrderProducts || []));
          }
        }

        const updatedOrderBook: IStorageItems = {
          orders,
          orderBookTimeline,
          selectedOrder,
          otherOrdersProductsIds,
          orderProductsIds: productIds,
          selectedOrderSummary: newSelectedOrderSummary,
        };

        set(updatedOrderBook);

        return affectedOrderProducts;
      },

      updateOtherOrdersProductsIds: (orderId: string, productIds: GetOrderProductIds) => {
        const { otherOrdersProductsIds } = get();
        if (!otherOrdersProductsIds) {
          return [{ orderId, orderProductsIds: productIds }];
        }

        const foundOrderProductId = otherOrdersProductsIds.find(
          (orderProduct) => orderProduct.orderId === orderId,
        );
        if (foundOrderProductId) {
          const newOtherOrdersProductsIds = otherOrdersProductsIds.filter(
            (orderProduct) => foundOrderProductId.orderId !== orderProduct.orderId,
          );
          const newfoundOrderProductIdValue = { orderId, orderProductsIds: productIds };
          newOtherOrdersProductsIds.push(newfoundOrderProductIdValue);
          return newOtherOrdersProductsIds;
        }

        return [...otherOrdersProductsIds, { orderId, orderProductsIds: productIds }];
      },

      reloadOrderItemsQuantityBook: async ({
        orderId,
        calculate,
      }: ReloadOrderItemsQuantityBookParams): Promise<IAffectedOrderProducts[] | undefined> => {
        const {
          selectedOrder,
          orders,
          orderBookTimeline,
          orderProductsIds,
          fetchOrderProductIds,
          updateOtherOrdersProductsIds,
        } = get();

        if (!selectedOrder) {
          return;
        }

        const updatedOrders = [...orders];
        const orderIndex = orders.findIndex((o) => o.id === orderId);

        const productIds: GetOrderProductIds = (await fetchOrderProductIds(orderId)) || {
          productColorIds: [],
          kitIds: [],
        };
        const otherOrdersProductsIds = updateOtherOrdersProductsIds(orderId, productIds);

        const affectedOrderProducts: IAffectedOrderProducts[] = [];
        if (calculate) {
          const calculateResponse = await getOrderSummary({ orderId });
          if (calculateResponse.data) {
            const summaryTotal = calculateResponse.data.calculate?.total;
            if (summaryTotal && orderIndex >= 0) {
              const { total, subtotal, pieces } = summaryTotal;
              updatedOrders[orderIndex] = {
                ...orders[orderIndex],
                subTotal: subtotal,
                totalPieces: pieces,
                totalValue: total,
              };
            }

            affectedOrderProducts.push(...(calculateResponse.data.affectedOrderProducts || []));
          }

          const updatedOrderBook = {
            orders: updatedOrders,
            orderBookTimeline,
            selectedOrder,
            orderProductsIds,
            otherOrdersProductsIds,
          };

          set(updatedOrderBook);

          return affectedOrderProducts;
        }
      },

      handleFinishSelectedOrder: (teamId) => {
        const {
          orders,
          selectedOrder,
          isOfflineMode,
          handleRemoveSelectedOrder,
          handleChangeSelectedOrder,
        } = get();

        if (orders.length > 0) {
          handleChangeSelectedOrder({ order: orders[0], teamId: teamId, finishedOrder: true });
          return;
        }

        if (selectedOrder) {
          removeOrderCatalogSnapshot({ orderId: selectedOrder.id, teamId, isOfflineMode });
        }

        handleRemoveSelectedOrder();
      },

      handleRemoveSelectedOrder: (team) => {
        const { handleChangeOrderBookTimeline, selectedOrder, isOfflineMode } = get();
        if (!selectedOrder) {
          return;
        }

        removeOrderCatalogSnapshot({ orderId: selectedOrder.id, teamId: team?.id, isOfflineMode });

        const updatedOrderBook = {
          orders: [],
          orderBookTimeline: OrderBookStateEnum.INITIAL_STATE,
          selectedOrder: undefined,
          selectedOrderSummary: undefined,
          orderProductsIds: {
            productColorIds: [],
            kitIds: [],
          },
        };

        set({
          ...updatedOrderBook,
        });
        handleChangeOrderBookTimeline(OrderBookStateEnum.INITIAL_STATE);
      },

      handleRemoveOrder: (orderId, teamId) => {
        const { orders, selectedOrder, isOfflineMode, handleFinishSelectedOrder } = get();

        if (orderId === selectedOrder?.id) {
          return handleFinishSelectedOrder(teamId);
        }

        removeOrderCatalogSnapshot({ orderId, teamId, isOfflineMode });

        const updatedOrders = orders.filter((order) => order.id !== orderId);

        set({ orders: updatedOrders });
      },
      toggleOrderValueVisibility: () => {
        const state = get();
        localStorage.setItem(StorageEnum.SHOW_ORDER_VALUE, String(!state.hideOrderValue));
        set({ ...state, hideOrderValue: !state.hideOrderValue });
      },
      handleChangeSalesOpportunityInterceptor: (id?: number) => {
        set({ salesOpportunityInterceptorId: id });
      },

      resetStore: (team) => {
        const { selectedOrder, orders, isOfflineMode } = get();

        for (const order of [selectedOrder, ...orders]) {
          if (order) {
            removeOrderCatalogSnapshot({ orderId: order.id, teamId: team?.id, isOfflineMode });
          }
        }

        set({
          loading: false,
          loadingOrder: false,
          orderAutocompleteFocused: false,
          loadingOrderItems: false,
          orderBookState: false,
          orderBookTimeline: OrderBookStateEnum.INITIAL_STATE,
          selectedOrder: undefined,
          selectedOrderItemsQuantity: 0,
          orderProductsIds: {
            productColorIds: [],
            kitIds: [],
          },
          customer: undefined,
          orders: [],
          lastCreatedOrderId: undefined,
          salesOpportunityInterceptorId: undefined,
          hideOrderValue: localStorage.getItem(StorageEnum.SHOW_ORDER_VALUE) === 'true',
        });
      },

      onChangeCustomer: (customer: OrderBookCustomer) => set({ customer }),
    }),
    {
      name: PERSISTER_KEY,
      skipHydration: true,
      partialize: (orderBook: OrderBookStore): OrderBookStorage => ({
        orders: orderBook.orders,
        selectedOrder: orderBook.selectedOrder ?? null,
        selectedOrderSummary: orderBook.selectedOrderSummary ?? null,
        orderBookTimeline: orderBook.orderBookTimeline,
        orderProductsIds: orderBook.orderProductsIds,
        otherOrdersProductsIds: orderBook.otherOrdersProductsIds,
        isOfflineMode: orderBook.isOfflineMode,
      }),
    },
  ),
);
export default createSelectors(useOrderBookStore);
